import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Check, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { setMeta } from "@/lib/seo";

const WA_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "91XXXXXXXXXX";
const WA_TEXT = "Hi, I want StayFlow Pro. My StayFlow login email is ___ and I want monthly/yearly.";
const WA_HREF = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_TEXT)}`;

const PRO_ADDS = [
  "Unlimited bookings you take yourself, billed in your own format, with no booking fee on any of them.",
  "StayFlow POS, kitchen display and digital menu — bill every plate and every drink, and send orders straight to the kitchen screen.",
  "AI Smart Booking — turn a screenshot of a guest's WhatsApp chat into a booking, ready to confirm.",
  "AI ID scan — point the camera at a guest's Aadhaar or passport and their record fills itself.",
  "Car-plate scan and AI dish images for your menu.",
];

const PRO_URL = "https://stayflowapp.in/api/public/pro";

const STEPS = [
  "Enter the email or phone you registered in StayFlow.",
  "Confirm it is your account and choose monthly or yearly.",
  "Pay. StayFlow Pro switches on immediately and your GST invoice appears in the app.",
];

const Pro = () => {
  useEffect(() => {
    setMeta({
      title: "StayFlow Pro — everything a host needs beyond free bookings",
      description:
        "StayFlow is free forever for verified hosts. Pro adds unlimited offline bookings, POS, KDS and AI tools for ₹499 a month or ₹4,999 a year, GST included.",
    });
  }, []);

  return (
    <div className="bg-paper min-h-screen" data-testid="pro-page">
      <Navbar />

      <main className="pt-[76px]">
        {/* Hero */}
        <section className="relative overflow-hidden bg-ink text-white">
          <div aria-hidden="true" className="absolute -top-32 -right-24 w-[34rem] h-[34rem] rounded-full bg-brand/25 blur-[120px]" />
          <div className="relative max-w-5xl mx-auto px-6 md:px-12 pt-20 pb-20 md:pt-28 md:pb-28">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <p className="overline text-brand">For hosts</p>
              <h1 className="mt-5 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl">
                StayFlow Pro
              </h1>
              <p className="mt-4 text-lg md:text-xl text-white/70 max-w-2xl">
                Keep more of every booking. Run your homestay like a pro.
              </p>
              <p className="mt-8 max-w-3xl text-white/60 leading-relaxed">
                StayFlow is free for every verified host, forever. Pro is for hosts who want every booking,
                every bill and every guest in one place.
              </p>
              <a
                href={PRO_URL}
                data-testid="pro-get-btn-hero"
                className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white text-sm font-medium min-h-12 py-3 px-6 text-center transition-transform duration-200 hover:-translate-y-0.5"
              >
                Get StayFlow Pro
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Free, forever */}
        <section className="max-w-5xl mx-auto px-6 md:px-12 pt-20 md:pt-28">
          <h2 className="font-heading font-bold text-ink text-base md:text-lg tracking-tight">Free, forever</h2>
          <p className="mt-6 max-w-3xl text-ink leading-relaxed" data-testid="pro-free-p1">
            Online booking links, calendar, guest records with ID, coupons and date blocks, your listing on the
            StayFlow marketplace, GST invoices, payouts straight to your bank, reports and staff logins. No monthly
            charge, ever.
          </p>
          <p className="mt-5 max-w-3xl text-ink leading-relaxed" data-testid="pro-free-p2">
            You pay only when a guest books and pays through StayFlow: 5% + GST on that booking. No booking, no
            fee. Travel portals typically keep 15–25% of every stay; StayFlow leaves the rest with you and handles
            the GST on those stays, so you can sell online without your own GST number.
          </p>
        </section>

        {/* Pro adds + price */}
        <section className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid md:grid-cols-12 gap-12 md:gap-16">
            <div className="md:col-span-7">
              <h2 className="font-heading font-bold text-ink text-base md:text-lg tracking-tight">StayFlow Pro — for the bookings portals never see</h2>
              <p className="mt-5 text-ink leading-relaxed" data-testid="pro-adds-intro">
                Most homestay bookings come by phone, WhatsApp and walk-in. The free plan records 3 of them a
                month. Pro records them all.
              </p>
              <ul className="mt-7 space-y-5" data-testid="pro-features">
                {PRO_ADDS.map((f, i) => (
                  <motion.li
                    key={f}
                    initial={{ opacity: 0, x: -12 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                    className="flex gap-3.5 text-ink"
                  >
                    <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-brand-soft text-brand-dark flex items-center justify-center">
                      <Check className="w-3.5 h-3.5" aria-hidden="true" />
                    </span>
                    <span className="leading-relaxed">{f}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-5">
              <div className="sticky top-28 rounded-3xl border border-black/10 bg-white p-7 md:p-8" data-testid="pro-price-card">
                <p className="overline text-ink-faint">One plan per property</p>
                <p className="mt-4 font-heading font-extrabold tracking-tighter text-ink text-4xl">₹499</p>
                <p className="text-ink-soft">per month</p>
                <div className="my-5 h-px bg-black/10" />
                <p className="font-heading font-bold text-ink text-2xl tracking-tight">₹4,999</p>
                <p className="text-ink-soft">per year</p>
                <p className="mt-1 text-xs text-brand-dark" data-testid="pro-yearly-saving">Save ₹989 a year</p>
                <p className="mt-5 text-sm text-ink-soft" data-testid="pro-price-note">
                  GST included — your GST invoice appears in your app as soon as you pay. Prepaid, no auto-debit,
                  no lock-in; unused days are not refunded. If you ever stop, you go back to the free plan and
                  nothing is deleted.
                </p>
                <a
                  href={PRO_URL}
                  data-testid="pro-get-btn"
                  className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white text-sm font-medium min-h-12 py-3 px-4 text-center transition-transform duration-200 hover:-translate-y-0.5"
                >
                  Get StayFlow Pro
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </a>
                <a
                  href={WA_HREF}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="pro-whatsapp-link"
                  className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs text-ink-soft hover:text-ink transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                  or message us on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* How to get it */}
        <section className="bg-paper-muted/60 border-y border-black/5">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24">
            <h2 className="font-heading font-bold text-ink text-base md:text-lg tracking-tight">How to get it:</h2>
            <ol className="mt-9 grid md:grid-cols-3 gap-8 md:gap-10" data-testid="pro-steps">
              {STEPS.map((s, i) => (
                <li key={i} className="relative">
                  <span className="font-mono text-sm text-brand">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-3 text-ink leading-relaxed">{s}</p>
                </li>
              ))}
            </ol>
            <div className="mt-12 flex flex-col items-start gap-3">
              <a
                href={PRO_URL}
                data-testid="pro-get-btn-2"
                className="group inline-flex items-center gap-2 rounded-full bg-ink text-white font-medium h-12 px-6 transition-transform duration-200 hover:-translate-y-0.5"
              >
                Get StayFlow Pro
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </a>
              <a
                href={WA_HREF}
                target="_blank"
                rel="noreferrer"
                data-testid="pro-whatsapp-link-2"
                className="inline-flex items-center gap-1.5 pl-1 text-xs text-ink-soft hover:text-ink transition-colors"
              >
                <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                or message us on WhatsApp
              </a>
            </div>
          </div>
        </section>

        {/* Terms */}
        <section className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="overline text-ink-faint">Terms</p>
          <p className="mt-4 max-w-3xl text-ink-soft leading-relaxed" data-testid="pro-terms">
            Prepaid for the period chosen, no auto-debit, no refund for unused days; when it ends you go back to
            the free plan and nothing is deleted.
          </p>
          <Link to="/stays" data-testid="pro-back-stays" className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-ink-soft hover:text-ink transition-colors">
            Browse stays on StayFlow
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Pro;
